import { apiClient } from '@/shared/services/api';

const BASE_PATH = '/api/v1/fennec';
const CMS_PATH = '/api/v1/cms/fennec';

const handleResponse = (response) => {
  if (response?.data?.success) {
    return { success: true, data: response.data.data, message: '' };
  }
  return { success: true, data: response.data, message: '' };
};

const handleError = (error, context) => {
  const message =
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    `Fennec: failed to fetch ${context}`;
  if (process.env.NODE_ENV !== 'production') {
    console.error(`[Fennec] ${context} error:`, message);
  }
  return { success: false, data: null, message };
};

// ─── Feature Flag ─────────────────────────────────────────────────

export const getFeatureFlag = async () => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/feature-flag`);
    return handleResponse(response);
  } catch (error) {
    return handleError(error, 'feature-flag');
  }
};

// ─── Toggle & Status ──────────────────────────────────────────────

export const toggleFennec = async (enabled, deviceId = null) => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/toggle`, { enabled, deviceId });
    return handleResponse(response);
  } catch (error) {
    return handleError(error, 'toggle');
  }
};

export const getStatus = async () => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/status`);
    return handleResponse(response);
  } catch (error) {
    return handleError(error, 'status');
  }
};

// ─── Feed ─────────────────────────────────────────────────────────

export const getFeed = async (cursor = null, limit = 20) => {
  try {
    const params = { limit };
    if (cursor) params.cursor = cursor;
    const response = await apiClient.get(`${BASE_PATH}/feed`, { params });
    return handleResponse(response);
  } catch (error) {
    return handleError(error, 'feed');
  }
};

export const getPinnedWidget = async () => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/pinned-widget`);
    return handleResponse(response);
  } catch (error) {
    return handleError(error, 'pinned-widget');
  }
};

// ─── Widgets ──────────────────────────────────────────────────────

export const getWidgetsLive = async () => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/widgets/live`);
    return handleResponse(response);
  } catch (error) {
    return handleError(error, 'widgets/live');
  }
};

export const getWidgetsUpcoming = async () => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/widgets/upcoming`);
    return handleResponse(response);
  } catch (error) {
    return handleError(error, 'widgets/upcoming');
  }
};

export const getWidgetsStandings = async () => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/widgets/standings`);
    return handleResponse(response);
  } catch (error) {
    return handleError(error, 'widgets/standings');
  }
};

export const getWidgetsScorers = async () => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/widgets/scorers`);
    return handleResponse(response);
  } catch (error) {
    return handleError(error, 'widgets/scorers');
  }
};

// ─── Sidebar ──────────────────────────────────────────────────────

export const getSidebarData = async () => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/sidebar`);
    return handleResponse(response);
  } catch (error) {
    return handleError(error, 'sidebar');
  }
};

// ─── Gamification ────────────────────────────────────────────────

export const getMissions = async () => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/missions`);
    return handleResponse(response);
  } catch (error) {
    return handleError(error, 'missions');
  }
};

export const getGamificationProfile = async () => {
  try {
    const response = await apiClient.get('/gamification/v2/profile');
    return { success: true, data: response.data?.data || response.data };
  } catch (error) {
    return handleError(error, 'gamification-profile');
  }
};

export const getLeaderboard = async () => {
  try {
    const response = await apiClient.get('/gamifaction/leaderboard');
    const raw = response.data?.data || response.data;
    const users = Array.isArray(raw) ? raw.map(u => ({
      userId: u.userId || u.id,
      name: u.user?.firstName ? `${u.user.firstName} ${u.user.lastName || ''}`.trim() : (u.name || u.username),
      avatar: u.user?.avatar || u.avatar || null,
      xp: u.score || u.xp || 0,
    })) : [];
    return { success: true, data: { users } };
  } catch (error) {
    return handleError(error, 'leaderboard');
  }
};

// ─── Stories ─────────────────────────────────────────────────────

export const getFennecStoryIds = async () => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/stories/fennec-ids`);
    return handleResponse(response);
  } catch (error) {
    return handleError(error, 'stories/fennec-ids');
  }
};

// ─── Analytics ────────────────────────────────────────────────────

export const trackEvent = async (eventType, sessionId, metadata = {}) => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/analytics/event`, {
      eventType,
      sessionId,
      ...metadata,
    });
    return handleResponse(response);
  } catch (error) {
    return handleError(error, 'analytics/event');
  }
};
